import { useEffect, useMemo, useState } from "react";
import { InterestDialog } from "@/components/InterestDialog";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Phone, Globe, Mail, Calendar, Plus, Eye, Heart, Camera, Play,
  MessageCircle, Navigation, BadgeCheck, ChevronRight, Clock,
  Users, Shirt, Building2, Trophy, ShieldCheck, Award, GraduationCap, Music, Languages, Info, UserCheck,
  Facebook, Instagram, Youtube, Check, Star, Linkedin, Music2,
  Send, Tag,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RouteMap } from "@/components/RouteMap";
import { Gallery } from "@/components/Gallery";
import { ShareButton } from "@/components/ShareButton";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, resolveUrl, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useFavorites } from "@/context/FavoritesContext";
import { AMENITY_LABEL } from "@/constants/businessMeta";

const HICON = { users: Users, shirt: Shirt, whistle: UserCheck, building: Building2, trophy: Trophy, shield: ShieldCheck, clock: Clock, award: Award, "map-pin": MapPin, graduation: GraduationCap, music: Music, languages: Languages, info: Info };
const SOCIAL_STYLE = {
  facebook: { Icon: Facebook, bg: "#1877F2" },
  instagram: { Icon: Instagram, bg: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" },
  youtube: { Icon: Youtube, bg: "#FF0000" },
  tiktok: { Icon: Music2, bg: "#000000" },
  linkedin: { Icon: Linkedin, bg: "#0A66C2" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); return d.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; }
};

const Section = ({ title, action, children, testid, navy }) => (
  <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid={testid}>
    {(title || action) && (
      <div className="flex items-center justify-between mb-4">
        {title && <h3 className={`font-display text-xl font-extrabold ${navy ? "text-[#0B2454]" : "text-slate-900"}`}>{title}</h3>}
        {action}
      </div>
    )}
    {children}
  </section>
);

const ActivityDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [interestOpen, setInterestOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [biz, setBiz] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryStart, setGalleryStart] = useState(0);
  const [galleryList, setGalleryList] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [tab, setTab] = useState("about");

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.get(`/businesses/${id}`),
      api.get(`/reviews/${id}`),
      api.get(`/events/${id}`).catch(() => ({ data: { upcoming: [], past: [] } })),
      api.get("/promotions/active").catch(() => ({ data: [] })),
    ]).then(([b, r, e, p]) => {
      if (!active) return;
      setBiz(b.data);
      setReviews(r.data);
      setEvents(e.data);
      setOffers(p.data.filter((o) => o.business_id && [id, b.data.id].includes(o.business_id)));
      if (["events", "offers"].includes(location.state?.tab)) setTab(location.state.tab);
    }).catch(() => {}).finally(() => active && setLoading(false));
    let vid = localStorage.getItem("sc_visitor_id");
    if (!vid) { vid = (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`); localStorage.setItem("sc_visitor_id", vid); }
    api.post(`/businesses/${id}/view`, { visitor_id: vid }).catch(() => {});
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    const st = location.state?.tab;
    if (loading || !["events", "offers"].includes(st) || tab !== st) return;
    const t = setTimeout(() => {
      const target = st === "offers" ? `offer-${location.state.promoId}` : `event-${location.state.eventId}`;
      const el = document.getElementById(target) || document.getElementById("detail-tabs");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [loading, tab, location.state]);

  const locations = useMemo(() => {
    if (!biz) return [];
    if (biz.locations?.length) return biz.locations;
    if (biz.lat != null && biz.lng != null) return [{ address: biz.address, lat: biz.lat, lng: biz.lng }];
    return [];
  }, [biz]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F8FB]"><Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-4">
          <div className="h-72 rounded-2xl bg-slate-200 animate-pulse" />
          <div className="h-40 rounded-2xl bg-slate-200 animate-pulse" />
        </div>
      </div>
    );
  }
  if (!biz) {
    return (
      <div className="min-h-screen bg-[#F6F8FB]"><Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <p className="text-lg font-semibold">{t("detail.notFound")}</p>
          <Link to="/" className="text-primary font-semibold mt-4 inline-block">{t("detail.return")}</Link>
        </div>
      </div>
    );
  }

  const fav = isFavorite(biz.id);
  const photos = biz.photos || [];
  const cover = biz.cover_image || photos[0];
  const dupes = biz.photo_dupes || [];
  const stripPhotos = photos.filter((p, i) => p !== cover && p !== biz.logo && !dupes.includes(i));
  const video = biz.video_url && /\.(mp4|m4v|mov|webm)(\?|$)/i.test(biz.video_url) ? biz.video_url : "";
  const contactsByType = (type) => (biz.contacts || []).filter((c) => c.type === type && c.value);
  const phones = contactsByType("phone");
  const emails = contactsByType("email");
  const websites = contactsByType("website");
  const whatsapps = [...contactsByType("viber"), ...contactsByType("whatsapp")];
  const viberHref = (v) => `viber://chat?number=${encodeURIComponent("+" + v.replace(/[^0-9]/g, "").replace(/^(?!30)(69)/, "30$1"))}`;
  const social = biz.social || {};
  const dest = locations[0];
  const directionsUrl = dest ? `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}` : null;
  const mapsUrl = dest ? `https://www.google.com/maps/search/?api=1&query=${dest.lat},${dest.lng}` : null;

  const onFav = () => { const added = toggleFavorite(biz.id); toast.success(added ? t("fav.saved") : t("fav.removed")); };
  const openGallery = (i, list = null) => { setGalleryList(list); setGalleryStart(i); setGalleryOpen(true); };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/reviews/${id}`, form);
      toast.success(t("detail.reviewAdded"));
      setReviewOpen(false);
      const [b, r] = await Promise.all([api.get(`/businesses/${id}`), api.get(`/reviews/${id}`)]);
      setBiz(b.data); setReviews(r.data);
      setForm({ name: "", rating: 5, comment: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    }
  };

  const hasSchedule = (biz.weekly_schedule_photos?.length > 0) || biz.weekly_schedule_text || (biz.opening_hours?.length > 0);
  const tabs = [
    stripPhotos.length && { key: "photos", label: t("detail.tabPhotos"), color: "text-orange-600 data-[state=active]:bg-orange-500" },
    hasSchedule && { key: "schedule", label: t("detail.tabSchedule"), color: "text-blue-700 data-[state=active]:bg-blue-600" },
    biz.facilities?.length && { key: "facilities", label: t("detail.tabFacilities"), color: "text-violet-700 data-[state=active]:bg-violet-600" },
    { key: "reviews", label: t("detail.reviews"), color: "text-emerald-700 data-[state=active]:bg-emerald-600" },
    (events.upcoming.length || events.past.length) && { key: "events", label: t("detail.tabNews"), color: "text-pink-700 data-[state=active]:bg-pink-600" },
    offers.length && { key: "offers", label: t("detail.tabOffers"), color: "text-amber-700 data-[state=active]:bg-amber-500" },
  ].filter(Boolean);
  const activeTab = tabs.find((x) => x.key === tab) ? tab : (tabs[0]?.key || "reviews");

  const QuickBtn = ({ icon: Icon, label, onClick, href, variant = "default", testid }) => {
    const cls = `flex-1 min-w-[120px] h-12 rounded-xl font-bold gap-2 ${
      variant === "default"
        ? "bg-primary text-white hover:bg-primary/90"
        : variant === "fav"
        ? (fav ? "border-2 border-rose-300 bg-rose-50 text-rose-600" : "border-2 border-primary bg-white text-primary hover:bg-primary/5")
        : "border-2 border-primary bg-white text-primary hover:bg-primary/5"
    }`;
    const content = <><Icon size={18} /> {label}</>;
    if (href) return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={`inline-flex items-center justify-center ${cls}`}>{content}</a>;
    return <Button onClick={onClick} data-testid={testid} className={cls}>{content}</Button>;
  };

  const auds = biz.audiences || [];
  const bgAudience = ["paidia", "enilikes"].includes(location.state?.audience) ? location.state.audience
    : auds.includes("paidia") || !auds.includes("enilikes") ? "paidia" : "enilikes";

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="activity-detail-page" data-audience={bgAudience}
      style={{ backgroundImage: `url(/bg/${bgAudience}.png)`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed", backgroundColor: bgAudience === "paidia" ? "#e8f3fb" : "#eaf6ec" }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-4 flex-wrap" data-testid="breadcrumb">
          <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"))} data-testid="detail-back-button" className="inline-flex items-center gap-1 hover:text-slate-800"><ArrowLeft size={15} /> {t("detail.back")}</button>
          {biz.category && <><ChevronRight size={14} /> <span className="truncate max-w-[200px]">{biz.category}</span></>}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* MAIN */}
          <div className="lg:col-span-2 space-y-6">
            {/* HERO */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="relative h-56 sm:h-80 bg-slate-200">
                {cover ? <img src={resolveUrl(cover)} alt={biz.name} className="w-full h-full object-cover cursor-pointer" onClick={() => openGallery(0)} /> : <div className="w-full h-full grid place-items-center text-slate-400"><Camera size={40} /></div>}
                {biz.logo && (
                  <img src={resolveUrl(biz.logo)} alt="" data-testid="activity-detail-logo" className="absolute -bottom-6 left-6 h-24 w-24 rounded-2xl object-cover ring-4 ring-white bg-white shadow-lg" />
                )}
              </div>
              {/* thumbnails */}
              {(stripPhotos.length > 0 || video) && (
                <div className="grid grid-cols-5 gap-2 p-3 pt-8">
                  {video && (
                    <button onClick={() => openGallery(0)} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-slate-800 group" data-testid="gallery-video-thumb">
                      {cover && <img src={resolveUrl(cover)} alt="" className="w-full h-full object-cover opacity-70" />}
                      <span className="absolute inset-0 grid place-items-center"><span className="h-10 w-10 rounded-full bg-white/90 grid place-items-center group-hover:scale-110 transition-transform"><Play size={18} className="text-slate-900 ml-0.5" fill="currentColor" /></span></span>
                    </button>
                  )}
                  {stripPhotos.slice(0, video ? 3 : 4).map((p, i) => (
                    <button key={i} onClick={() => openGallery(i + (video ? 1 : 0))} className="relative aspect-[4/3] rounded-lg overflow-hidden group" data-testid={`gallery-thumb-${i}`}>
                      <img src={resolveUrl(p)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </button>
                  ))}
                  <button onClick={() => openGallery(0)} data-testid="gallery-view-all" className="aspect-[4/3] rounded-lg bg-slate-800 text-white grid place-items-center text-center p-1 hover:bg-slate-700">
                    <span className="flex flex-col items-center text-xs font-semibold"><Camera size={18} className="mb-1" /> {t("detail.viewAllPhotos")} ({stripPhotos.length})</span>
                  </button>
                </div>
              )}
            </div>

            {/* BASICS */}
            <div className="space-y-3">
              {(biz.tags?.length > 0 || biz.subcategory) && (
                <div className="flex flex-wrap gap-2" data-testid="business-tags">
                  {biz.category && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 font-semibold">{biz.category}</Badge>}
                  {(biz.tags || []).map((tg, i) => <Badge key={i} variant="secondary" className="font-semibold">{tg}</Badge>)}
                </div>
              )}
              <div className="flex items-start justify-between gap-3">
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2454] flex items-center gap-2" data-testid="activity-detail-title">
                  {biz.name}
                  {biz.verified && <BadgeCheck size={24} className="text-primary shrink-0" data-testid="verified-badge" />}
                </h1>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={onFav} data-testid="activity-detail-favorite-btn" className={`h-11 w-11 grid place-items-center rounded-full border ${fav ? "border-rose-300 bg-rose-50" : "border-border bg-white hover:bg-muted"}`}>
                    <Heart size={18} className={fav ? "fill-rose-500 text-rose-500" : "text-slate-600"} />
                  </button>
                  <ShareButton title={biz.name} />
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm flex-wrap">
                {biz.rating > 0 && <span className="flex items-center gap-1.5 font-bold"><StarRating value={biz.rating} size={15} /> {biz.rating.toFixed(1)} <span className="text-slate-500 font-normal">({biz.reviews_count} {t("detail.reviewsWord")})</span></span>}
                <span className="flex items-center gap-1 text-slate-500"><Eye size={15} /> {(biz.views || 0).toLocaleString("el-GR")} {t("detail.views")}</span>
              </div>
              {biz.description && <p className="text-slate-600 leading-relaxed">{biz.description}</p>}

              {/* QUICK ACTIONS */}
              <div className="flex flex-wrap gap-2.5 pt-1" data-testid="quick-actions">
                {phones[0] && <QuickBtn icon={Phone} label={t("detail.contactHeading")} href={`tel:${phones[0].value}`} testid="qa-contact" />}
                {websites[0] && <QuickBtn icon={Globe} label={t("detail.website")} href={websites[0].value.startsWith("http") ? websites[0].value : `https://${websites[0].value}`} variant="outline" testid="qa-website" />}
                {directionsUrl && <QuickBtn icon={Navigation} label={t("detail.directions")} href={directionsUrl} variant="outline" testid="qa-directions" />}
                {whatsapps[0] && <QuickBtn icon={MessageCircle} label="Viber" href={viberHref(whatsapps[0].value)} variant="viber" testid="qa-viber" />}
                <QuickBtn icon={Heart} label={t("detail.save")} onClick={onFav} variant="fav" testid="qa-save" />
              </div>
            </div>

            {/* HIGHLIGHTS */}
            {biz.highlights?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="highlights">
                {biz.highlights.map((h, i) => {
                  const Icon = HICON[h.icon] || Info;
                  return (
                    <div key={i} className="p-2 text-center" data-testid={`highlight-${i}`}>
                      <span className="mx-auto mb-1.5 grid place-items-center text-[#0757C9]"><Icon size={38} strokeWidth={2.2} /></span>
                      <p className="font-bold text-sm text-slate-900 leading-tight">{h.title}</p>
                      {h.value && <p className="text-xs text-slate-500 mt-0.5 leading-tight">{h.value}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ABOUT (fixed) */}
            {biz.about && (
              <Section title={t("detail.aboutTitle")} testid="about-section">
                <div className="grid sm:grid-cols-5 gap-5 items-start">
                  <div className="sm:col-span-3">
                    <p className={`text-slate-700 leading-relaxed whitespace-pre-wrap ${aboutExpanded ? "" : "line-clamp-6"}`}>{biz.about}</p>
                    {biz.about.length > 280 && (
                      <button onClick={() => setAboutExpanded((v) => !v)} data-testid="about-toggle" className="mt-2 text-primary font-semibold text-sm">{aboutExpanded ? t("detail.showLess") : t("detail.readMore")}</button>
                    )}
                  </div>
                  {photos[0] && <img src={resolveUrl(photos[0])} alt="" className="sm:col-span-2 rounded-xl w-full h-40 object-cover" />}
                </div>
              </Section>
            )}

            {/* DEPARTMENTS (fixed, below About) */}
            {biz.departments?.length > 0 && (
              <Section title={t("detail.tabDepartments")} testid="departments-section">
                <div className="grid sm:grid-cols-2 gap-4">
                  {biz.departments.map((d, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden" data-testid={`department-${i}`}>
                      {d.image && <img src={resolveUrl(d.image)} alt={d.name} className="w-full h-36 object-cover" />}
                      <div className="p-4">
                        <h4 className="font-display font-bold text-slate-900">{d.name}</h4>
                        {d.description && <p className="text-sm text-slate-600 mt-1">{d.description}</p>}
                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                          {d.ages && <p className="flex items-center gap-1.5"><Users size={14} className="text-primary" /> {d.ages}</p>}
                          {d.level && <p className="flex items-center gap-1.5"><GraduationCap size={14} className="text-primary" /> {d.level}</p>}
                          {(d.days || d.hours) && <p className="flex items-center gap-1.5"><Clock size={14} className="text-primary" /> {[d.days, d.hours].filter(Boolean).join(" · ")}</p>}
                          {d.location && <p className="flex items-center gap-1.5"><MapPin size={14} className="text-primary" /> {d.location}</p>}
                          {d.cost && <p className="flex items-center gap-1.5"><Award size={14} className="text-primary" /> {d.cost}</p>}
                          {d.slots && <p className="text-xs text-green-600 font-semibold">{d.slots}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* TABS */}
            <Tabs value={activeTab} onValueChange={setTab} id="detail-tabs" className="scroll-mt-24">
              <TabsList className="w-full justify-start overflow-x-auto h-auto flex-wrap bg-white border border-slate-200 rounded-xl p-1" data-testid="content-tabs">
                {tabs.map((tb) => <TabsTrigger key={tb.key} value={tb.key} data-testid={`tab-${tb.key}`} className={`rounded-lg font-extrabold text-[15px] px-4 py-2 data-[state=active]:text-white data-[state=active]:shadow-md transition-colors ${tb.color}`}>{tb.label}</TabsTrigger>)}
              </TabsList>

              {biz.about && (
                <TabsContent value="about" className="mt-4" />
              )}

              {biz.departments?.length > 0 && (
                <TabsContent value="departments" className="mt-4" />
              )}

              {hasSchedule && (
                <TabsContent value="schedule" className="mt-4 space-y-4">
                  {biz.weekly_schedule_photos?.length > 0 ? (
                    <Section title={t("detail.schedule")} testid="tab-content-schedule">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{biz.weekly_schedule_photos.map((p, i) => <img key={i} src={resolveUrl(p)} alt="" className="rounded-xl w-full object-cover cursor-pointer" onClick={() => openGallery(i, biz.weekly_schedule_photos)} data-testid={`schedule-photo-${i}`} />)}</div>
                    </Section>
                  ) : biz.weekly_schedule_text ? (
                    <Section title={t("detail.schedule")} testid="tab-content-schedule"><pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{biz.weekly_schedule_text}</pre></Section>
                  ) : null}
                  {biz.opening_hours?.length > 0 && (
                    <Section title={t("detail.openingHours")} navy testid="opening-hours-main"><OpeningHoursTable hours={biz.opening_hours} closedLabel={t("detail.closed")} /></Section>
                  )}
                </TabsContent>
              )}

              {biz.facilities?.length > 0 && (
                <TabsContent value="facilities" className="mt-4">
                  <Section title={t("detail.tabFacilities")} testid="tab-content-facilities">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {biz.facilities.map((f, i) => (
                        <div key={i} className="text-center" data-testid={`facility-${i}`}>
                          {f.image ? <img src={resolveUrl(f.image)} alt={f.label} className="w-full h-24 object-cover rounded-xl" /> : <div className="w-full h-24 rounded-xl bg-slate-100 grid place-items-center text-slate-400"><Building2 /></div>}
                          <p className="mt-1.5 font-semibold text-sm text-slate-900 leading-tight">{f.label}</p>
                          {f.note && <p className="text-xs text-slate-500 leading-tight">{f.note}</p>}
                        </div>
                      ))}
                    </div>
                  </Section>
                </TabsContent>
              )}

              {stripPhotos.length > 0 && (
                <TabsContent value="photos" className="mt-4">
                  <Section title={t("detail.tabPhotos")} testid="tab-content-photos">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {stripPhotos.map((p, i) => <button key={i} onClick={() => openGallery(i + (video ? 1 : 0))} className="aspect-square rounded-xl overflow-hidden group"><img src={resolveUrl(p)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></button>)}
                    </div>
                  </Section>
                </TabsContent>
              )}

              <TabsContent value="reviews" className="mt-4">
                <Section title={`${t("detail.reviews")} (${reviews.length})`} navy testid="tab-content-reviews"
                  action={<Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setReviewOpen(true)} data-testid="activity-add-review-button"><Plus size={16} /> {t("detail.addReview")}</Button>}>
                  {reviews.length === 0 ? <p className="text-slate-500 text-sm">{t("detail.noReviews")}</p> : (
                    <div className="space-y-4">
                      {reviews.map((r) => (
                        <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0" data-testid="review-item">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-900">{r.name}</span>
                            <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                          </div>
                          <StarRating value={r.rating} size={13} />
                          {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </TabsContent>

              {(events.upcoming.length > 0 || events.past.length > 0) && (
                <TabsContent value="events" className="mt-4 space-y-4">
                  {events.upcoming.length > 0 && (
                    <Section title={t("detail.upcomingEvents")} testid="tab-content-events">
                      <div className="space-y-3">{events.upcoming.map((e) => <EventCard key={e.id} ev={e} onOpen={openGallery} highlight={location.state?.eventId === e.id} />)}</div>
                    </Section>
                  )}
                  {events.past.length > 0 && (
                    <Section title={t("detail.pastEvents")} testid="past-events">
                      <div className="space-y-3 opacity-75">{events.past.map((e) => <EventCard key={e.id} ev={e} past onOpen={openGallery} highlight={location.state?.eventId === e.id} />)}</div>
                    </Section>
                  )}
                </TabsContent>
              )}

              {offers.length > 0 && (
                <TabsContent value="offers" className="mt-4">
                  <Section title={t("detail.tabOffers")} testid="tab-content-offers">
                    <div className="space-y-3">{offers.map((o) => <OfferCard key={o.id} offer={o} onOpen={openGallery} highlight={location.state?.promoId === o.id} />)}</div>
                  </Section>
                </TabsContent>
              )}
            </Tabs>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
            {/* Location */}
            {(locations.length > 0 || biz.address) && (
              <Section title={t("detail.location")} navy testid="sidebar-location">
                {(biz.address || dest?.address) && <p className="text-sm text-slate-600 flex items-start gap-1.5 mb-2"><MapPin size={16} className="mt-0.5 shrink-0 text-primary" /> {biz.address || dest?.address}{biz.postal_code ? `, ${biz.postal_code}` : ""}</p>}
                {locations.length > 0 ? <RouteMap locations={locations} name={biz.name} /> : null}
                <div className="flex gap-2 mt-3">
                  {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="sidebar-google-maps" className="flex-1 h-10 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-slate-50"><MapPin size={15} /> Google Maps</a>}
                  {directionsUrl && <a href={directionsUrl} target="_blank" rel="noopener noreferrer" data-testid="sidebar-directions" className="flex-1 h-10 rounded-xl bg-primary text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"><Navigation size={15} /> {t("detail.directions")}</a>}
                </div>
              </Section>
            )}

            {/* Contact */}
            {(
              <Section title={t("detail.contactHeading")} navy testid="sidebar-contact">
                <div className="space-y-2.5">
                  {phones.map((p, i) => <ContactRow key={`p${i}`} icon={Phone} value={p.value} href={`tel:${p.value}`} action={t("detail.call")} testid="contact-phone" />)}
                  {emails.map((e, i) => <ContactRow key={`e${i}`} icon={Mail} value={e.value} href={`mailto:${e.value}`} action="Email" testid="contact-email" />)}
                  {websites.map((w, i) => <ContactRow key={`w${i}`} icon={Globe} value={w.value} href={w.value.startsWith("http") ? w.value : `https://${w.value}`} action={t("detail.website")} testid="contact-website" />)}
                  {whatsapps.map((w, i) => <ContactRow key={`vb${i}`} icon={MessageCircle} value={w.value} href={viberHref(w.value)} action="Viber" testid="contact-viber" />)}
                  <button type="button" onClick={() => setInterestOpen(true)} data-testid="contact-interest-btn"
                    className="w-full h-11 rounded-xl bg-[#3E63DD] text-white text-sm font-bold inline-flex items-center justify-center gap-2 hover:brightness-110 transition-[filter]">
                    <Send size={16} fill="currentColor" /> Εκδήλωση Ενδιαφέροντος
                  </button>
                </div>
              </Section>
            )}

            {/* Opening hours */}
            {biz.opening_hours?.length > 0 && (
              <Section title={t("detail.openingHours")} navy testid="sidebar-hours"><OpeningHoursTable hours={biz.opening_hours} closedLabel={t("detail.closed")} /></Section>
            )}

            {/* Social */}
            {Object.values(social).some(Boolean) && (
              <Section title={t("detail.socialMedia")} testid="sidebar-social">
                <div className="flex gap-3 flex-wrap">
                  {Object.entries(social).filter(([, v]) => v).map(([k, v]) => {
                    const s = SOCIAL_STYLE[k] || { Icon: Globe, bg: "#64748b" };
                    const Icon = s.Icon;
                    return (
                      <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`} className="flex flex-col items-center gap-1 text-xs text-slate-600">
                        <span className="h-11 w-11 grid place-items-center rounded-xl text-white shadow-sm transition-transform hover:scale-110" style={{ background: s.bg }}><Icon size={20} /></span>
                        <span className="capitalize">{k}</span>
                      </a>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Amenities */}
            {biz.amenities?.length > 0 && (
              <Section title={t("detail.amenities")} navy testid="sidebar-amenities">
                <div className="grid grid-cols-2 gap-2">
                  {biz.amenities.map((a, i) => <p key={i} className="flex items-center gap-1.5 text-sm text-slate-700"><Check size={15} className="text-green-600 shrink-0" /> {AMENITY_LABEL[a] || a}</p>)}
                </div>
              </Section>
            )}
            {/* Reviews summary (sidebar) */}
            <Section title={t("detail.reviews")} navy testid="sidebar-reviews"
              action={<Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setReviewOpen(true)} data-testid="sidebar-add-review"><Plus size={15} /> {t("detail.addReview")}</Button>}>
              {biz.rating > 0 ? (
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl font-extrabold text-slate-900">{biz.rating.toFixed(1)}</span>
                  <div>
                    <StarRating value={biz.rating} size={15} />
                    <p className="text-xs text-slate-500">{biz.reviews_count} {t("detail.reviewsWord")}</p>
                  </div>
                </div>
              ) : <p className="text-sm text-slate-500 mb-2">{t("detail.noReviews")}</p>}
              <div className="space-y-3">
                {reviews.slice(0, 3).map((r) => (
                  <div key={r.id} className="border-t border-slate-100 pt-2 first:border-0 first:pt-0" data-testid="sidebar-review-item">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-slate-900">{r.name}</span>
                      <StarRating value={r.rating} size={11} />
                    </div>
                    {r.comment && <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </Section>
          </aside>
        </div>
      </main>

      {/* Sticky mobile action bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 p-2.5 flex gap-2" data-testid="mobile-action-bar">
        {phones[0] && <a href={`tel:${phones[0].value}`} className="flex-1 h-11 rounded-xl bg-primary text-white font-bold inline-flex items-center justify-center gap-1.5 text-sm"><Phone size={16} /> {t("detail.call")}</a>}
        {directionsUrl && <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 font-bold inline-flex items-center justify-center gap-1.5 text-sm"><Navigation size={16} /> {t("detail.directions")}</a>}
        <button onClick={onFav} className={`h-11 w-11 shrink-0 rounded-xl border grid place-items-center ${fav ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}><Heart size={18} className={fav ? "fill-rose-500 text-rose-500" : "text-slate-600"} /></button>
      </div>

      <InterestDialog open={interestOpen} onOpenChange={setInterestOpen} business={biz} />
      <Gallery photos={galleryList || stripPhotos} videoUrl={galleryList ? null : video} open={galleryOpen} startIndex={galleryStart} onClose={() => setGalleryOpen(false)} />

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{t("detail.reviewTitle")}</DialogTitle></DialogHeader>
          <form onSubmit={submitReview} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("detail.reviewName")}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" className="w-full h-11 px-4 rounded-xl bg-muted outline-none focus:ring-2 ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("detail.reviewRating")}</label>
              <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}><Star size={28} className={n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} /></button>)}</div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("detail.reviewComment")}</label>
              <textarea rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment-input" className="w-full px-4 py-3 rounded-xl bg-muted outline-none focus:ring-2 ring-primary resize-none" />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid="review-submit-button">{t("detail.submit")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

const ContactRow = ({ icon: Icon, value, href, action, testid }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="flex items-center gap-2 text-sm text-slate-700 truncate"><Icon size={16} className="text-primary shrink-0" /> <span className="truncate">{value}</span></span>
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className="shrink-0 h-8 px-3 rounded-lg bg-primary/10 text-primary text-xs font-bold inline-flex items-center">{action}</a>
  </div>
);

const OpeningHoursTable = ({ hours, closedLabel }) => (
  <table className="w-full text-sm" data-testid="opening-hours-table">
    <tbody>
      {hours.map((h, i) => (
        <tr key={i} className="border-b border-slate-100 last:border-0">
          <td className="py-1.5 text-slate-600">{h.day}</td>
          <td className="py-1.5 text-right font-semibold text-slate-900">{h.closed ? closedLabel : `${h.open || "—"} – ${h.close || "—"}`}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const EventCard = ({ ev, past, onOpen, highlight }) => (
  <div id={`event-${ev.id}`} className={`flex gap-3 rounded-xl border p-2 scroll-mt-28 ${highlight ? "border-pink-400 ring-2 ring-pink-200" : "border-slate-200"}`} data-testid="event-card">
    {ev.cover ? (
      <button type="button" onClick={() => onOpen(0, [ev.cover])} className="shrink-0 rounded-lg overflow-hidden group" data-testid="event-cover-btn">
        <img src={resolveUrl(ev.cover)} alt={ev.title} className="h-20 w-20 object-cover group-hover:scale-105 transition-transform" />
      </button>
    ) : <div className="h-20 w-20 rounded-lg bg-slate-100 grid place-items-center text-slate-400 shrink-0"><Calendar /></div>}
    <div className="flex-1 min-w-0">
      <h4 className="font-bold text-slate-900 leading-tight">{ev.title}</h4>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Calendar size={12} /> {fmtDate(ev.date)}{ev.time ? ` · ${ev.time}` : ""}{ev.location ? ` · ${ev.location}` : ""}</p>
      {ev.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line break-words" data-testid="event-description">{ev.description}</p>}
      {!past && ev.cta_link && ev.cta_label && <a href={ev.cta_link} target="_blank" rel="noopener noreferrer" className="inline-block mt-1.5 text-primary font-semibold text-sm">{ev.cta_label} →</a>}
    </div>
  </div>
);

const OfferCard = ({ offer, onOpen, highlight }) => (
  <div id={`offer-${offer.id}`} className={`flex gap-3 rounded-xl border p-2 scroll-mt-28 ${highlight ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`} data-testid="offer-card">
    {offer.image ? (
      <button type="button" onClick={() => onOpen(0, [offer.image])} className="shrink-0 rounded-lg overflow-hidden group" data-testid="offer-image-btn">
        <img src={resolveUrl(offer.image)} alt={offer.title} className="h-20 w-20 object-cover group-hover:scale-105 transition-transform" />
      </button>
    ) : <div className="h-20 w-20 rounded-lg bg-amber-50 grid place-items-center text-amber-500 shrink-0"><Tag /></div>}
    <div className="flex-1 min-w-0">
      <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-extrabold tracking-wide">ΠΡΟΣΦΟΡΑ</span>
      <h4 className="font-bold text-slate-900 leading-tight mt-1">{offer.title}</h4>
      {offer.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line break-words" data-testid="offer-description">{offer.description}</p>}
    </div>
  </div>
);

export default ActivityDetailPage;
